// StopInput — Phase 4 Plan 04-03 (D-75, DIS-07).
//
// HERE Places Autosuggest with frozen geocoding:
//   - default UI: <input> debounced 300ms → GET autosuggest.search.hereapi.com
//   - once a suggestion is picked, value receives address + lat + lng +
//     formatted_address and renders as a "frozen pill" — editing the address
//     text after pick does NOT re-trigger autosuggest (user must click X to
//     clear and search again).
//   - Advanced toggle reveals 3 manual inputs (address text + lat + lng) for
//     off-grid coordinates.
//
// CSP: connect-src 'self' includes autosuggest.search.hereapi.com (Wave 0
// migration of Caddyfile.prod).
//
// Env: import.meta.env.VITE_HERE_API_KEY (see PREREQS §HERE Maps).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TOUCH_TARGET_MIN } from "../lib/tokens";
import { LabeledField, fieldInputStyle } from "./LabeledField";

export interface StopAddress {
  address: string;
  lat: number | null;
  lng: number | null;
  formatted_address?: string | null;
}

export interface StopInputProps {
  value: StopAddress;
  onChange: (next: StopAddress) => void;
  locale?: string;
  index?: number;
}

interface HereSuggestion {
  id: string;
  title: string;
  address?: { label?: string };
  position?: { lat: number; lng: number };
}

// Sofia center default (D-75 reference) used as `at=` param when no prior
// center known. Once a stop is picked, subsequent stops can reuse its position
// (caller-controlled — MVP keeps this simple).
const DEFAULT_CENTER = { lat: 42.7, lng: 23.3 };

const HERE_AUTOSUGGEST_URL = "https://autosuggest.search.hereapi.com/v1/autosuggest";

export function StopInput({
  value,
  onChange,
  locale = "en",
  index = 0,
}: StopInputProps): React.JSX.Element {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>(value.address ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [suggestions, setSuggestions] = useState<HereSuggestion[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  const isFrozen = value.lat !== null && value.lng !== null;

  // Sync search input to incoming value (e.g. on re-mount).
  useEffect(() => {
    setSearchInput(value.address ?? "");
  }, [value.address]);

  // Debounce search input (300ms).
  useEffect(() => {
    if (isFrozen) {
      setDebouncedQuery("");
      return;
    }
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, isFrozen]);

  // Fire HERE Autosuggest call.
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    // Vite injects env vars on `import.meta.env.*` at build time. Cast through
    // an indexed access object to satisfy TypeScript without polluting the
    // global ImportMeta interface (vite-env.d.ts may not be present in the
    // worktree's tsconfig include set).
    const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
    const apiKey: string | undefined = meta?.env?.VITE_HERE_API_KEY;
    if (!apiKey) {
      // No key configured — silently skip autosuggest and let user use Advanced.
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    const params = new URLSearchParams({
      q: debouncedQuery,
      at: `${DEFAULT_CENTER.lat},${DEFAULT_CENTER.lng}`,
      limit: "8",
      types: "address,city,place",
      lang: locale,
      apiKey,
    });
    fetch(`${HERE_AUTOSUGGEST_URL}?${params.toString()}`, {
      signal: ctrl.signal,
    })
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        return resp.json() as Promise<{ items?: HereSuggestion[] }>;
      })
      .then((data) => {
        setSuggestions(data.items ?? []);
        setErrorMsg(null);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setSuggestions([]);
        setErrorMsg(
          t(
            "dispatcher:newtrip.here_error",
            "Couldn't fetch suggestions. Use 'Show advanced' to enter lat/lng manually.",
          ) as string,
        );
      });
    return () => ctrl.abort();
  }, [debouncedQuery, locale, t]);

  function handlePick(s: HereSuggestion): void {
    if (!s.position) return;
    onChange({
      address: s.address?.label ?? s.title,
      lat: s.position.lat,
      lng: s.position.lng,
      formatted_address: s.address?.label ?? s.title,
    });
    setSearchInput(s.address?.label ?? s.title);
    setSuggestions([]);
  }

  function handleClear(): void {
    onChange({ address: "", lat: null, lng: null, formatted_address: null });
    setSearchInput("");
    setSuggestions([]);
  }

  function handleManualAddress(addr: string): void {
    onChange({ ...value, address: addr, formatted_address: addr });
    setSearchInput(addr);
  }

  function handleManualLat(latStr: string): void {
    const latNum = Number.parseFloat(latStr);
    onChange({ ...value, lat: Number.isFinite(latNum) ? latNum : null });
  }

  function handleManualLng(lngStr: string): void {
    const lngNum = Number.parseFloat(lngStr);
    onChange({ ...value, lng: Number.isFinite(lngNum) ? lngNum : null });
  }

  return (
    <div data-testid={`stop-input-${index}`} style={{ marginBottom: 8 }}>
      {isFrozen ? (
        <div
          data-testid={`stop-input-frozen-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#F1F5F9",
            padding: 10,
            borderRadius: 8,
            minHeight: TOUCH_TARGET_MIN,
          }}
        >
          <span style={{ flex: 1, fontSize: 14 }}>
            {value.formatted_address ?? value.address}
            <small style={{ display: "block", color: "#666", fontSize: 12 }}>
              {value.lat?.toFixed(5)}, {value.lng?.toFixed(5)}
            </small>
          </span>
          <button
            type="button"
            onClick={handleClear}
            data-testid={`stop-input-clear-${index}`}
            aria-label={t("dispatcher:newtrip.stop_clear", "Clear address") as string}
            style={{
              minWidth: TOUCH_TARGET_MIN,
              minHeight: TOUCH_TARGET_MIN,
              background: "transparent",
              border: "1px solid #ccc",
              borderRadius: 8,
              fontSize: 18,
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          <LabeledField label={t("dispatcher:newtrip.address_label", "Address") as string}>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("dispatcher:newtrip.stop_placeholder", "Search address…") as string}
              data-testid={`stop-input-search-${index}`}
              style={fieldInputStyle}
            />
          </LabeledField>
          {errorMsg ? (
            <div
              data-testid={`stop-input-error-${index}`}
              role="alert"
              style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}
            >
              {errorMsg}
            </div>
          ) : null}
          {suggestions.length > 0 && (
            <ul
              data-testid={`stop-input-suggestions-${index}`}
              style={{
                background: "white",
                border: "1px solid #ddd",
                borderRadius: 8,
                margin: "4px 0 0 0",
                padding: 0,
                listStyle: "none",
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {suggestions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(s)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      minHeight: TOUCH_TARGET_MIN,
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid #eee",
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {s.title}
                    {s.address?.label ? (
                      <small style={{ display: "block", color: "#666" }}>{s.address.label}</small>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <button
        type="button"
        onClick={() => setAdvancedOpen((prev) => !prev)}
        data-testid={`stop-input-advanced-toggle-${index}`}
        style={{
          marginTop: 6,
          background: "transparent",
          border: "none",
          color: "#1E63CE",
          fontSize: 12,
          cursor: "pointer",
          padding: 4,
        }}
      >
        {advancedOpen
          ? (t("dispatcher:newtrip.advanced_hide", "Hide advanced") as string)
          : (t("dispatcher:newtrip.advanced_show", "Show advanced") as string)}
      </button>
      {advancedOpen && (
        <div
          data-testid={`stop-input-advanced-${index}`}
          style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}
        >
          <input
            type="text"
            value={value.address}
            onChange={(e) => handleManualAddress(e.target.value)}
            placeholder={t("dispatcher:newtrip.address_manual", "Address") as string}
            data-testid={`stop-input-manual-address-${index}`}
            style={{ padding: 8, border: "1px solid #ccc", borderRadius: 6 }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="number"
              step="any"
              value={value.lat ?? ""}
              onChange={(e) => handleManualLat(e.target.value)}
              placeholder="lat"
              min={-90}
              max={90}
              data-testid={`stop-input-manual-lat-${index}`}
              style={{ flex: 1, padding: 8, border: "1px solid #ccc", borderRadius: 6 }}
            />
            <input
              type="number"
              step="any"
              value={value.lng ?? ""}
              onChange={(e) => handleManualLng(e.target.value)}
              placeholder="lng"
              min={-180}
              max={180}
              data-testid={`stop-input-manual-lng-${index}`}
              style={{ flex: 1, padding: 8, border: "1px solid #ccc", borderRadius: 6 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
